import { BCMSCurrency, BCMSDate, BCMSDefaultModel, BCMSRecord, Link, ModalOpen, useWorkflowStep } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from "compid/custom-ui/CustomSelect";
import ButtonContainer from "compid/shared/containers/ButtonContainer";
import PageLayoutContainer from "compid/shared/containers/PageLayoutContainer";
import CenterColumnContainer from "compid/shared/containers/PageLayoutContainer/containers/CenterColumnContainer";
import PageView from "compid/shared/containers/PageViewContainer";
import BillerView from "compid/shared/views/BillerView";
import { DatePickerView } from "compid/ui/DatePicker";
import UIAmountInput from "compid/ui/UIAmountInput";
import UIButton from "compid/ui/UIButton";
import UIToggle from "compid/ui/UIToggle";
import { getDefault, getWorkflowSteps } from "compid/workflows/ReminderWorkflow";
import { Bill, Reminder } from "model";
import React from "react";
import "./styles.css";

/**
 * @function AddReminderPage
 * @return {React.Component}
 * @exported true
 * @description The main component for AddReminderPage
 */

export default function AddReminderPage(props) {
  const workflowStep = useWorkflowStep(getDefault, getWorkflowSteps);
  BCMSRecord.globalControllerFor([Reminder, Bill], workflowStep.globalController);
  const reminder = workflowStep.read("reminder");

  const workflowState = workflowStep.getStepState;
  const billReceivedFrequency = workflowState.userDidSetBillReceivedFrequency ? reminder.billReceivedFrequency : "";
  const daysToRemindMeInAdvanced = workflowState.userDidSetDaysToRemindMeInAdvanced ? String(reminder.daysToRemindMeInAdvanced) : "";
  const frequencies = {
    "weekly": 7,
    "every 2 weeks": 14,
    "every 4 weeks": 28,
    "twice a month": 15,
    "monthly": 30,
    "every 2 months": 60,
    "every 3 months": 90,
    "every 6 months": 180,
    "annually": 365
  };

  const daysRemindMeInAdvanced = ["03", "05", "10", "14", "21", "28"];
  const frequencyAsNum = frequencies[billReceivedFrequency];

  const update = prop => value => {
    if (prop === "typicalDueDate") {
      workflowStep.update(["userDidSetTypicalDueDate"], true);
    } else if (prop === "billReceivedFrequency") {
      workflowStep.update(["userDidSetBillReceivedFrequency"], true);
    } else if (prop === "daysToRemindMeInAdvanced") {
      workflowStep.update(["userDidSetDaysToRemindMeInAdvanced"], true);
    }

    workflowStep.update(["reminder", [prop]], value);
  };

  const setReminder = () => {
    const dueDate = BCMSDate.init(reminder.typicalDueDate);
    const defaultReminder = BCMSDefaultModel.reminder();
    var updatedReminder = BCMSDefaultModel.mergeRecords(reminder, defaultReminder);
    updatedReminder = Reminder.init(updatedReminder);
    updatedReminder.dueDate = dueDate;
    updatedReminder.email = reminder.email;
    const defaultBill = BCMSDefaultModel.bill();
    defaultBill.biller = updatedReminder.biller;
    defaultBill.id = Bill.nextID();
    defaultBill.status = "due";
    defaultBill.amount = updatedReminder.amount;
    defaultBill.balance = updatedReminder.balance;
    defaultBill.minDue = updatedReminder.amount;
    defaultBill.date = dueDate;
    defaultBill.reminder = updatedReminder
    workflowStep.update(["reminder"], updatedReminder);

    BCMSRecord.batch([
      // Create a reminder, which references a bill and vice versa.
      Reminder.create(updatedReminder),
      // Create a new bill for this biller.
      Bill.create(defaultBill)
    ], workflowStep.globalController);

    // Route to the next step
    try {
      window.scrollTo(0, 0);
      workflowStep.workflow.nextStep(workflowStep.getStepState);
    } catch (error) {
      workflowStep.navigate(`/addReminderConfirmation/${reminder.biller.id}/${workflowStep.currentStep + 1}`, { state: workflowStep.getStepState });
    }
  };

  // "Leave" button is clicked in the "Are you sure you want to leave?" modal.
  const onLeave = () => workflowStep.navigate(`/billerDetails/details/${reminder.biller.id}`);

  const customSelectItemDays = daysRemindMeInAdvanced.reduce((acc, daysInAdvanced, index) => {
    // If frequency has been selected, filter "Days to remind me in advanced" items.
    const inAdvanced = parseInt(daysInAdvanced);

    if (frequencyAsNum > daysInAdvanced) {
      acc.push(
        <CustomSelectItem key={index} value={`${inAdvanced}`} dataPoint={`Day_In_Advanced_${inAdvanced}`}>{daysInAdvanced} days</CustomSelectItem>
      );
      // Else if no frequency has been selected, show all "Days to remind me in advanced" items.
    } else if (frequencyAsNum === undefined) {
      acc.push(
        <CustomSelectItem key={index} value={`${inAdvanced}`} dataPoint={`Day_In_Advanced_${inAdvanced}`}>{daysInAdvanced} days</CustomSelectItem>
      );
    }
    return acc;
  }, []);

  return (
    <PageView className="add-reminder-page">
      <p className="page-sub-header semi-bold-text">ADD REMINDERS</p>

      <PageLayoutContainer>
        <div className="flex-container flex-vertical flex-width">
          <div className="flex-height flex-container flex-vertical upper-content-container">
            <BillerView biller={reminder.biller} />
          </div>

          <div className="flex-height flex-width flex-container all-center">
            <CenterColumnContainer>
              <div className="form-group flex-container flex-vertical">
                <div className="form-row">
                  {/*<DatePickerView helptext="Numeric date starting with the month" />*/}
                  <DatePickerView disableToday={false} disableWeekends={false} setDate={workflowState.userDidSetTypicalDueDate ? reminder.typicalDueDate : undefined} showWords={false} dateLabel="Typical due date"
                    onChange={update("typicalDueDate")} dataPoint="Add_Reminder_Date">
                    <span>Numeric date starting with the month</span>
                  </DatePickerView>
                </div>

                <div className="amount-input-container">
                  <UIAmountInput placeHolder="Typical amount due" dataPoint="Add_Reminder_Amount"
                    value={BCMSCurrency.init(reminder.amount).format()} onChange={update("amount")} />
                </div>

                <div className="form-row custom-select-form-row">
                  <CustomSelect label="Bill received" value={billReceivedFrequency} helpText="" onChange={update("billReceivedFrequency")} dataPoint="Add_Reminder_Bill_Recieved">
                    <CustomSelectItem value="weekly">Weekly</CustomSelectItem>
                    <CustomSelectItem value="every 2 weeks">Every 2 weeks</CustomSelectItem>
                    <CustomSelectItem value="every 4 weeks">Every 4 weeks</CustomSelectItem>
                    <CustomSelectItem value="twice a month">Twice a month</CustomSelectItem>
                    <CustomSelectItem value="monthly" dataPoint="Bill_Recieved_Monthly_Item">Monthly</CustomSelectItem>
                    <CustomSelectItem value="every 2 months">Every 2 months</CustomSelectItem>
                    <CustomSelectItem value="every 3 months">Every 3 months</CustomSelectItem>
                    <CustomSelectItem value="every 6 months">Every 6 months</CustomSelectItem>
                    <CustomSelectItem value="annually">Annually</CustomSelectItem>
                  </CustomSelect>
                </div>
                <div className="form-row custom-select-form-row">
                  <CustomSelect label="Remind me in advance" value={daysToRemindMeInAdvanced} helpText="" onChange={update("daysToRemindMeInAdvanced")} dataPoint="Add_Reminder_In_Advanced">
                    {customSelectItemDays}
                  </CustomSelect>
                </div>
              </div>

              <div className="email-notifications-container">
                <p>Send email notifications to <b>{ reminder.email }</b> when the payment:</p>
                <label className="flex-container flex-horizontal align-center space-between is-due clickable">
                  <p data-point="Reminder_Is_Due">Is due</p>
                  <UIToggle text="" checked={ reminder.notificationDue } onChange={ update("notificationDue") } dataPoint="Reminder_Is_Due_Toggle" />
                </label>
                <label className="flex-container flex-horizontal align-center space-between has-been-sent clickable">
                  <p data-point="Reminder_Has_Been_Sent">Has been sent</p>
                  <UIToggle text="" checked={ reminder.notificationSent } onChange={ update("notificationSent") } dataPoint="Reminder_Has_Been_Sent_Toggle" />
                </label>
                <label className="flex-container flex-horizontal align-center space-between not-paid-by-due-date clickable">
                  <p data-point="Reminder_Paid_By_Due_Date">Isn't paid by the due date</p>
                  <UIToggle text="" checked={ reminder.notificationNotPaid } onChange={ update("notificationNotPaid") } dataPoint="Reminder_Paid_By_Due_Date_Toggle" />
                </label>
              </div>

              <ButtonContainer>
                <UIButton className="secondary-button">
                  <ModalOpen id="WantToLeaveModal" data={{ onLeave: onLeave }}>Cancel</ModalOpen>
                </UIButton>
                <UIButton className="primary-button" onClick={setReminder} dataPoint="Add_Reminder_Set_Reminder">Set reminders</UIButton>
              </ButtonContainer>

              <Link className="text-center semi-bold-text add-autopay-link link-button" to={`/addAutopayWorkflow/${reminder.biller.id}`}>
                Add autopay instead?
              </Link>

            </CenterColumnContainer>
          </div>
        </div>
      </PageLayoutContainer>
    </PageView>
  );
}